import { Field } from 'formik';
import type { FormikHelpers } from 'formik';
import { NumericFormat } from 'react-number-format';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess, hidesPurchasePrices } from 'utils/helpers/access/hasUserAccess';
import FormArticleInput from 'components/forms/FormArticleInput';
import InputPriceRate from 'components/others/InputPriceRate';
import DimensionSelect from '../../DimensionSelect';
import VendorSelect from '../../VendorSelect';
import CatalogCategorySelect from '../../../CatalogCategory/CatalogCategorySelect';
import type { ProductEditType } from '../../types';
import type { SelectOption } from '../../types';

interface ProductFormGeneralProps {
    isSubmitting: boolean;
    setFieldValue: FormikHelpers<ProductEditType>['setFieldValue'];
    values: ProductEditType;
    product: ProductEditType;
}

const ProductFormGeneral = ({ setFieldValue, values, product }: ProductFormGeneralProps) => {
    const { auth } = useAuth();
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const hidePurchase = hidesPurchasePrices(auth?.user);
    const { t } = useTranslation(['Products', 'common']);
    const isCreate = Number(product?.ID || values?.ID || 0) === 0;

    return (
        <>
            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.name')}</label>
                <Field name="prodName" disabled={!canEdit} className="form-control" />
            </Form.Group>

            <FormArticleInput
                label={t('fields.article')}
                name="prodArticle"
                disabled={!isCreate || !canEdit}
                canGenerate={isCreate && canEdit}
                onChange={(article) => setFieldValue('prodArticle', article)}
            />

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.category')}</label> 
                <CatalogCategorySelect 
                    kind="product"
                    value={values.categoryId}
                    disabled={!canEdit}
                    onChange={(opt) => setFieldValue('categoryId', opt?.value || 0)}
                />
            </Form.Group>

            {hidePurchase ? null : (
            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.price')}</label>
                <InputPriceRate
                    value={values.prodDefPrice || 0}
                    onValueChange={(val) => {
                        setFieldValue('prodDefPrice', val.floatValue);
                    }} 
                />
            </Form.Group>
            )}

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.priceSale')}</label>
                <InputPriceRate
                    value={values.prodDefPriceSale || 0}
                    onValueChange={(val) => {
                        setFieldValue('prodDefPriceSale', val.floatValue);
                    }}
                /> 
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.vendor')}</label>
                <VendorSelect
                    required={false}
                    value={values.vendorId}
                    onChange={(opt: SelectOption | null) => {
                        setFieldValue('vendorId', opt?.value);
                    }}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.stockLimit')}</label>
                <NumericFormat
                    className="form-control"
                    value={values.prodStockLimit ?? 0}
                    disabled={!canEdit}
                    allowNegative={false}
                    decimalScale={0}
                    thousandSeparator=" "
                    allowLeadingZeros={false}
                    onValueChange={(val) => {
                        const next = val.floatValue;
                        const whole = next == null || !Number.isFinite(next) ? 0 : Math.round(next);
                        setFieldValue('prodStockLimit', whole < 0 ? 0 : whole);
                    }}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.dimension')}</label>
                <DimensionSelect
                    required={false}
                    value={values.dimensionId}
                    onChange={(opt: SelectOption | null) => {
                        setFieldValue('dimensionId', opt?.value);
                    }}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.description')}</label>
                <Field
                    name="prodDescription"
                    as="textarea"
                    rows="4"
                    disabled={!canEdit}
                    className="form-control"
                />
            </Form.Group>
        </>
    );
};

export default ProductFormGeneral;
